
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import enTranslations from '@/lib/i18n/en.json';
import noTranslations from '@/lib/i18n/no.json';
import { useAuth } from '@/hooks/useAuth';

type Language = 'en' | 'no';
type Translations = Record<string, any>;

interface LanguageContextType {
  language: Language;
  translations: Translations;
  setLanguage: (language: Language) => void;
  translate: (key: string) => string;
  updateTranslation: (language: Language, path: string, value: string) => void;
  allTranslations: {
    en: Translations;
    no: Translations;
  };
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { user } = useAuth();
  
  // Try to get saved language from localStorage or default to English
  const [language, setLanguageState] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    return savedLanguage || 'en';
  });

  const [translations, setTranslations] = useState<Translations>(language === 'en' ? enTranslations : noTranslations);
  const [allTranslations, setAllTranslations] = useState<{ en: Translations, no: Translations }>({
    en: enTranslations,
    no: noTranslations
  });

  // Load user profile language preference when user logs in
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (user) {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('language, dark_mode, full_name')
            .eq('id', user.id)
            .maybeSingle();
          
          if (error) {
            console.error('Error loading user preferences:', error);
            return;
          }
          
          // If we found saved preferences
          if (data) {
            if (data.language) {
              const userLang = data.language as Language;
              setLanguageState(userLang);
              localStorage.setItem('language', userLang);
            }
            
            // Also update dark mode if available
            if (data.dark_mode !== undefined && data.dark_mode !== null) {
              localStorage.setItem('darkMode', data.dark_mode.toString());
              
              // Apply dark mode class to html element
              if (data.dark_mode) {
                document.documentElement.classList.add('dark');
              } else {
                document.documentElement.classList.remove('dark');
              }
            }
          }
        } catch (error) {
          console.error('Failed to load user preferences:', error);
        }
      }
    };
    
    loadUserPreferences();
  }, [user]);

  // When language changes, update translations and save to localStorage and user profile
  const setLanguage = async (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    
    // Save to user profile if logged in
    if (user) {
      try {
        const { error } = await supabase
          .from('profiles')
          .update({ 
            language: lang,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);
        
        if (error) {
          console.error('Error saving language preference:', error);
        }
      } catch (error) {
        console.error('Failed to save language preference:', error);
      }
    }
  };

  useEffect(() => {
    setTranslations(language === 'en' ? allTranslations.en : allTranslations.no);
  }, [language, allTranslations]);

  // Get a translation by dot notation path
  const translate = (path: string): string => {
    try {
      const keys = path.split('.');
      let result: any = translations;
      
      for (const key of keys) {
        if (result[key] === undefined) {
          console.warn(`Translation key not found: ${path}`);
          return path;
        }
        result = result[key];
      }
      
      if (typeof result === 'string') {
        return result;
      }
      
      console.warn(`Translation result is not a string: ${path}`);
      return path;
    } catch (error) {
      console.error('Translation error:', error);
      return path;
    }
  };

  // Update a specific translation
  const updateTranslation = (lang: Language, path: string, value: string) => {
    try {
      const keys = path.split('.');
      const lastKey = keys.pop();
      
      if (!lastKey) return;
      
      const newTranslations = { ...allTranslations };
      let current = newTranslations[lang];
      
      // Navigate to the correct nested object
      for (const key of keys) {
        if (current[key] === undefined) {
          current[key] = {};
        }
        current = current[key];
      }
      
      // Update the value
      current[lastKey] = value;
      setAllTranslations(newTranslations);
      
      // If the current language is being updated, update translations too
      if (lang === language) {
        setTranslations(newTranslations[lang]);
      }
    } catch (error) {
      console.error('Error updating translation:', error);
    }
  };

  return (
    <LanguageContext.Provider 
      value={{ 
        language, 
        translations, 
        setLanguage, 
        translate, 
        updateTranslation,
        allTranslations 
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    console.error('useLanguage must be used within a LanguageProvider');
    // Return a default implementation to prevent crashes
    return {
      language: 'en',
      translations: enTranslations,
      setLanguage: () => console.warn('LanguageProvider not found'),
      translate: (key) => {
        console.warn(`Translation key not found: ${key} (LanguageProvider not found)`);
        return key;
      },
      updateTranslation: () => console.warn('LanguageProvider not found'),
      allTranslations: {
        en: enTranslations,
        no: noTranslations
      }
    };
  }
  return context;
};
